import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { publicApi } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck, ArrowRight, Quote } from "lucide-react";

const FALLBACK = {
  hero_eyebrow: "THE HOUSE",
  hero_title: "A Louisiana house, built on trust and time.",
  hero_subtitle: "Le Collectif Horloger began at a kitchen table in Baton Rouge — a dealer, a loupe, and one rule: never sell a watch you wouldn't put on your own wrist.",
  hero_image: "https://images.unsplash.com/photo-1495704907664-81f74a7efd9b?w=1920&q=80",
  founder_name: "Beau Dugas",
  founder_role: "Founder & Principal",
  founder_portrait: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=1000&q=80",
  founder_bio: [],
  philosophy_title: "How we think about watches",
  philosophy_intro: "",
  values: [],
  auth_title: "Authentication-first",
  auth_intro: "",
  auth_steps: [],
  pull_quote: "A watch keeps time. A house keeps its word.",
  pull_quote_attrib: "Beau Dugas, Founder",
  cta_title: "Come sit at the table.",
  cta_subtitle: "Join the VIP list for first calls on grails, or tell us exactly what you're hunting.",
};

export default function About() {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.get("/public/content/about")
      .then((r) => setC({ ...FALLBACK, ...r.data }))
      .catch(() => setC(FALLBACK))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !c) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-5 py-28 space-y-6">
          <Skeleton className="h-4 w-32 bg-[var(--panel-2)]" />
          <Skeleton className="h-12 w-3/4 bg-[var(--panel-2)]" />
          <Skeleton className="h-20 w-full bg-[var(--panel-2)]" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          {c.hero_image && <img src={c.hero_image} alt="" className="w-full h-full object-cover opacity-25" />}
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--overlay)] via-[var(--overlay)] to-[var(--bg)]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 py-28 md:py-36">
          <div className="text-[#c9a86a] tracking-[0.35em] text-xs mb-6">{c.hero_eyebrow}</div>
          <h1 className="text-4xl md:text-5xl font-semibold leading-tight text-[var(--text)] max-w-3xl" data-testid="about-title">{c.hero_title}</h1>
          <p className="text-[var(--text-2)] mt-6 max-w-2xl text-lg leading-relaxed">{c.hero_subtitle}</p>
        </div>
      </section>

      {/* Founder */}
      <section className="max-w-6xl mx-auto px-5 py-20" data-testid="about-founder">
        <div className="grid md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-2">
            <div className="aspect-[4/5] rounded-lg overflow-hidden border border-[var(--border-lch)] bg-[var(--panel-2)]">
              {c.founder_portrait && <img src={c.founder_portrait} alt={c.founder_name} className="w-full h-full object-cover" />}
            </div>
            <div className="mt-4">
              <div className="text-[var(--text)] font-medium">{c.founder_name}</div>
              <div className="text-[#c9a86a] text-sm">{c.founder_role}</div>
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">THE FOUNDER</div>
            <h2 className="text-2xl md:text-3xl font-semibold text-[var(--text)] mb-5">Why this house exists</h2>
            <div className="space-y-4">
              {(c.founder_bio || []).map((p, i) => (
                <p key={i} className="text-[var(--text-2)] leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy / values */}
      <section className="border-y border-[var(--border-lch)] bg-[var(--bg-2)]" data-testid="about-values">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="max-w-2xl mb-12">
            <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">OUR PHILOSOPHY</div>
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">{c.philosophy_title}</h2>
            <p className="text-[var(--text-2)] mt-4 leading-relaxed">{c.philosophy_intro}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {(c.values || []).map((v, i) => (
              <div key={i} className="border border-[var(--border-lch)] rounded-lg p-6 bg-[var(--panel)]" data-testid={`value-${i}`}>
                <div className="text-[var(--text)] font-medium">{v.title}</div>
                <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authentication-first */}
      <section className="max-w-6xl mx-auto px-5 py-20" data-testid="about-auth">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="text-[#c9a86a]" size={20} />
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs">AUTHENTICATION-FIRST</div>
        </div>
        <h2 className="text-3xl md:text-4xl font-semibold text-[var(--text)] max-w-3xl">{c.auth_title}</h2>
        <p className="text-[var(--text-2)] mt-4 max-w-2xl leading-relaxed">{c.auth_intro}</p>
        <div className="grid md:grid-cols-4 gap-5 mt-10">
          {(c.auth_steps || []).map((s, i) => (
            <div key={i} className="relative" data-testid={`auth-step-${i}`}>
              <div className="text-[#c9a86a]/40 text-4xl font-semibold">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-[var(--text)] font-medium mt-2">{s.title}</div>
              <p className="text-sm text-[var(--muted)] mt-1.5 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pull quote */}
      {c.pull_quote && (
        <section className="border-y border-[var(--border-lch)] bg-[var(--bg-2)]">
          <div className="max-w-4xl mx-auto px-5 py-16 text-center">
            <Quote className="text-[#c9a86a] mx-auto mb-5" size={28} />
            <p className="text-2xl md:text-3xl text-[var(--text)] font-light italic leading-snug">“{c.pull_quote}”</p>
            {c.pull_quote_attrib && <div className="text-[#c9a86a] text-sm mt-5 tracking-wide">{c.pull_quote_attrib}</div>}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-5 py-24 text-center" data-testid="about-cta">
        <h2 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">{c.cta_title}</h2>
        <p className="text-[var(--text-2)] mt-4 max-w-xl mx-auto">{c.cta_subtitle}</p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button onClick={() => navigate("/vip")} data-testid="about-join-vip"
            className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold tracking-wide rounded-none px-8 py-6 text-base">
            Join the VIP List <ArrowRight size={18} className="ml-2" />
          </Button>
          <Button onClick={() => navigate("/request")} data-testid="about-request" variant="outline"
            className="border-[var(--border-2)] text-[var(--text)] hover:bg-[var(--panel-2)] hover:text-[var(--text)] rounded-none px-8 py-6 text-base bg-transparent">
            Request a Watch
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
}
